import {
  AdditionalDiscount,
  AdultDetail,
  AirSearch,
  BookingAPIEndpoints,
  BookingsRequest,
  CompareDiscountsRequest,
  Discount,
  ImagePath,
  InsuranceRequest,
  Item,
  LoyaltyCard,
  NotificationsRequest,
  PassengerDetail,
  PassengerDetails,
  PriceToBookRequest,
  RequestBodyType,
  ServiceCharge,
  URLParams,
  Webform,
} from '../../../../interfaces/src/lib/interfaces/index';
import {
  Airfare,
  AirfareRequest,
  ChildrenDetails,
  CruiseDay,
  Day,
  DiscountType,
  FilterOptionSliceType,
  FirstCabinGuests,
  GuestData,
  Hit,
  InsuranceItem,
  ItineraryDetailsData,
  ItineraryResponse,
  OrderByTypeEnum,
  SecondCabinGuests,
  Segment,
} from '../../../../interfaces/src/lib/types/index';
import {
  BlockStatusEnum,
  ClassCodeFlightEnum,
  OriginTypeFlightEnum,
} from '../../../../interfaces/src/lib/enums/index';
import {
  CabinSelectionsType,
  ECouponType,
  EmergencyContact,
} from '../stateManager/slices/bookingStepsSlice';
import {
  getDealsFilters,
  getPaymentButtonAmountPaymentLabel,
  getPaymentButtonMerchantName,
  getStrikethroughDiscountCodes,
  getTimeFormat,
  isEnableApiPaymentKlarna,
  isProduction,
  isChain2,
} from './contextAwareUtils';
import { store, RootState } from '../stateManager/store';
import {
  getBestVoyagersClubMember,
  getPassengersData,
  getPromotionAges,
} from '../bookingFunnelUtils/bookingFunnelUtils';
import { getPaymentMethodForPriceToBook } from './klarnaUtils';
import { useWindowSize } from '../hooks/hooks';
import { breakpoints } from '../genericUtils/genericUtils';
import { SearchResponse } from '../../lib/utilitiesService/SearchService';
import dayjs from 'dayjs';

const mapOccupancy = (
  occupancy: string
): {
  adultsOccupancy: number;
  childrenOccupancy: number;
  infantOccupancy: number;
  cabinAccessible: boolean;
} => {
  const [adults, children, infants, cabinAccessible] = occupancy.split('|');
  return {
    adultsOccupancy: Number(adults),
    childrenOccupancy: Number(children),
    infantOccupancy: Number(infants),
    cabinAccessible: cabinAccessible === 'true',
  };
};

export const getSearchParams = (searchParams: URLSearchParams): URLParams => {
  let adultsOccupancy = 2,
    childrenOccupancy = 0,
    infantOccupancy = 0,
    cabinAccessible = false,
    adultsOccupancy2 = 0,
    childrenOccupancy2 = 0,
    infantOccupancy2 = 0,
    cabinAccessible2 = false;

  if (searchParams.get('occupancy1')) {
    ({ adultsOccupancy, childrenOccupancy, infantOccupancy, cabinAccessible } = mapOccupancy(
      getQueryParamIgnoreCase(window.location.search, 'occupancy1') || ''
    ));
  }

  if (searchParams.get('occupancy2')) {
    ({
      adultsOccupancy: adultsOccupancy2,
      childrenOccupancy: childrenOccupancy2,
      infantOccupancy: infantOccupancy2,
      cabinAccessible: cabinAccessible2,
    } = mapOccupancy(getQueryParamIgnoreCase(window.location.search, 'occupancy2') || ''));
  }
  const isDeal = getQueryParamIgnoreCase(window.location.search, 'isDeal');

  return {
    passengerType: getQueryParamIgnoreCase(window.location.search, 'paxType') ?? '',
    cabinPrice: getQueryParamIgnoreCase(window.location.search, 'cabinPrice') ?? '',
    adultPrice: getQueryParamIgnoreCase(window.location.search, 'adultPrice') ?? '',
    date: getQueryParamIgnoreCase(window.location.search, 'date') ?? '',
    cruiseID: getQueryParamIgnoreCase(window.location.search, 'cruiseID') ?? '',
    cabinType: getQueryParamIgnoreCase(window.location.search, 'cabinType') ?? '',
    categoryKey: getQueryParamIgnoreCase(window.location.search, 'categoryKey') ?? '',
    priceType: getQueryParamIgnoreCase(window.location.search, 'priceType') ?? '',
    priceCode: getQueryParamIgnoreCase(window.location.search, 'priceCode') ?? '',
    discountCode: getQueryParamIgnoreCase(window.location.search, 'discountCode') ?? '',
    promotionId: getQueryParamIgnoreCase(window.location.search, 'promotionID') ?? '',
    adultsOccupancy,
    childrenOccupancy,
    infantOccupancy,
    cabinAccessible,
    adultsOccupancy2,
    childrenOccupancy2,
    infantOccupancy2,
    cabinAccessible2,
    isConnectedCabin:
      getQueryParamIgnoreCase(window.location.search, 'isConnectedCabin') === 'true',
    isFlightIncluded: getQueryParamIgnoreCase(window.location.search, 'isFlightIncluded') ?? '',
    departureairportcode:
      getQueryParamIgnoreCase(window.location.search, 'departureairportcode') ?? '',
    departureairportname:
      getQueryParamIgnoreCase(window.location.search, 'departureairportname') ?? '',
    destinationairportcode:
      getQueryParamIgnoreCase(window.location.search, 'destinationairportcode') ?? '',
    isDeal: isDeal ? isDeal === 'true' : false,
    mandatoryInsuranceCode:
      getQueryParamIgnoreCase(window.location.search, 'mandatoryInsuranceCode') ?? '',
  };
};

export const getRequestBody = ({
  passengerType,
  discounts,
  selection,
  isFlightIncluded,
  priceType,
  priceCode,
  adultsOccupancy,
  childrenOccupancy,
  infantOccupancy,
  isPhysicallyChallenged,
  cabinTypes,
  categoryKey,
  cabinPreferences,
  ageRule,
  additionalDiscounts,
  cabinIndex = 0,
}: RequestBodyType) => {
  const state = store?.getState();
  const membershipInfo = state?.bookingStepsStore?.firstCabinSelections?.voyagersMembersInfo;
  const departureDate = state?.cruiseData?.departureDate;
  const passengersData = !state.bookingParams.isDeal
    ? state.bookingStepsStore?.firstCabinSelections?.passengersData
    : [];

  const promotionAges = getPromotionAges(ageRule);

  const { adultsSaved, minorsSaved, infantsSaved } = getPassengersData(
    passengersData,
    cabinIndex,
    adultsOccupancy,
    childrenOccupancy,
    infantOccupancy,
    promotionAges
  );

  // Determine adults
  /**
   * In order to understand the real age of the adult guest we need to check:
   *    - Scenario 1: pax data is saved and membership info is available --> this scenario is a step back from the checkout
   *        but the pax updates his membership info, so we need to get the age related to the membership
   *    - Scenario 2: pax data is saved, but no membership info --> this scenario is a step back from the checkout
   *        so the pax data is saved and the age is already calculated, so we can use it
   *    - Scenario 3: pax data not saved, but membership info is available --> this scenario is the first landing on
   *        the pax details step and a voyagers membership is just applied before going to the checkout step: we need to take
   *        the age related to the membership
   *    - Scenario 4: pax data not saved and no membership info --> this scenario is the happy flow, so if there's a promotion
   *        we need to send the default age of the promotion (age limit) instead of the default age of an adult (30)
   */
  const adults: GuestData[] = adultsSaved?.length
    ? adultsSaved?.map((adult) => {
        const member = membershipInfo.find(
          (member) => Number(member?.guestIndex) + 1 === adult.number
        );
        if (member?.memberInfo?.dateOfBirth && departureDate) {
          return {
            age: getAge(new Date(member?.memberInfo?.dateOfBirth), new Date(departureDate)),
          };
        } else {
          return { age: adult.age };
        }
      })
    : Array.from({ length: adultsOccupancy }, (_, index) => {
        const dateOfBirth = membershipInfo?.[index]?.memberInfo?.dateOfBirth;

        if (dateOfBirth && departureDate) {
          return { age: getAge(new Date(dateOfBirth), new Date(departureDate)) };
        } else {
          return { age: promotionAges?.adultAge || 30 };
        }
      });

  // Determine children (minor + infant)
  const children: GuestData[] = [...(minorsSaved || []), ...(infantsSaved || [])]?.length
    ? [...(minorsSaved || []), ...(infantsSaved || [])]?.map((child) => ({
        age: child.age,
      }))
    : [
        ...Array(childrenOccupancy).fill({ age: promotionAges?.minorAge || 10 }),
        ...Array(infantOccupancy).fill({ age: promotionAges?.infantAge || 1 }),
      ];

  const baseRequest: any = {
    isFlightIncluded,
    discounts,
    passengerInformation: {
      ...(isPhysicallyChallenged && { isPhysicallyChallenged: isPhysicallyChallenged }),
      passengerType: passengerType,
      adults: {
        total: adults.length,
        details: adults,
      },
      children: {
        total: children.length,
        details: children,
      },
    },
  };

  switch (selection) {
    case BookingAPIEndpoints.PRICETYPES:
    case BookingAPIEndpoints.CRUISES:
      baseRequest.cabinTypes = cabinTypes;
      break;
    case BookingAPIEndpoints.CABINPREFERENCES:
      baseRequest.priceTypes = [
        {
          id: priceType,
        },
      ];
      baseRequest.cabinTypes = cabinTypes;
      break;
    case BookingAPIEndpoints.CABINTYPES:
      baseRequest.priceTypes = [
        {
          id: priceType,
        },
      ];
      break;
    case BookingAPIEndpoints.CATEGORIES:
      baseRequest.priceCodes = [{ code: priceCode }];
      baseRequest.cabinTypes = cabinTypes;
      baseRequest.cabinPreferences = cabinPreferences;

      if (categoryKey) {
        baseRequest.categories = !Array.isArray(categoryKey)
          ? [{ code: categoryKey }]
          : categoryKey.map((code) => ({ code }));
      }

      baseRequest.priceTypes = [
        {
          id: priceType, //OK
        },
      ];
      break;
    default:
      break;
  }

  // Add the best voyagers club information to request body
  if (membershipInfo.length > 0) {
    const bestVoyagersMember = getBestVoyagersClubMember();
    baseRequest.loyaltyCard = {
      number: bestVoyagersMember?.memberInfo?.membership?.number || '',
      dateOfBirth: bestVoyagersMember?.memberInfo?.dateOfBirth || '',
      ...(bestVoyagersMember?.memberInfo?.name && { name: bestVoyagersMember?.memberInfo?.name }),
      ...(bestVoyagersMember?.memberInfo?.surname && {
        surname: bestVoyagersMember?.memberInfo?.surname,
      }),
    };
  }

  // Add additionalDiscounts if length > 0
  if (additionalDiscounts && additionalDiscounts.length > 0) {
    baseRequest.additionalDiscounts = additionalDiscounts;
  }

  return baseRequest;
};

// Helper function to convert 'DD/MM/YYYY' to 'YYYY-MM-DD'
export const convertToISOFormatUtil = (date: string): string => {
  if (!date) return '';
  const [day, month, year] = date.split('/');
  return `${year}-${month}-${day}`;
};

const createAirObject = (
  dateParams: string,
  originAir: string,
  destinationAirport: string,
  selectedAirfare: Airfare | undefined
): AirSearch | null => {
  // Split the string into departure and return dates
  const [rawDepartureDate, rawReturnDate] = dateParams ? dateParams.split('-') : [];

  // Convert both dates
  const departureDate = convertToISOFormatUtil(rawDepartureDate);
  const returnDate = convertToISOFormatUtil(rawReturnDate);

  if (!selectedAirfare) return null;

  // Build and return the 'air' object
  return {
    searchType:
      selectedAirfare?.return?.segments?.[0]?.bookingType ||
      selectedAirfare?.outbound?.segments?.[0]?.bookingType,
    origin: originAir,
    originType: OriginTypeFlightEnum.AIRPORT,
    destination: destinationAirport, // destinationAirportCode from URL param
    departureDate,
    returnDate,
    classCode: selectedAirfare?.price?.classCode, // Options: BUSINESS_CLASS, FIRST_CLASS, PREMIUM_CLASS, ECONOMY
    isOneWay: !selectedAirfare?.return, // If return airfare is present, it's not a one-way trip
    airfare: {
      packageCode: selectedAirfare?.outbound?.packageCode || '',
      outbound: {
        classCode: selectedAirfare?.outbound?.classCode || '', // Options: BUSINESS_CLASS, FIRST_CLASS, PREMIUM_CLASS, ECONOMY
        segments:
          selectedAirfare?.outbound?.segments?.map((seg: Segment) => ({
            airline: {
              code: seg.airline.code,
              name: seg.airline.name,
              description: seg.airline.description,
            },
            arrivalDate: seg.arrivalDate,
            blockStatus: seg.blockStatus, // Options: MANUAL_FIT_FLIGHT, OTHER
            bookingType: seg.bookingType,
            cabinClassDescription: seg.cabinClassDescription,
            departureDate: seg.departureDate,
            departureTime: seg.departureTime,
            fareClassCode: seg.fareClassCode,
            id: seg.id,
            legNumber: seg.legNumber,
            number: seg.number,
            segmentNumber: seg.segmentNumber,
            origin: {
              airport: {
                code: seg.origin.airport.code,
                name: seg.origin.airport.name,
              },
            },
            blockType: seg.blockType ?? '',
            destination: {
              airport: {
                code: seg.destination.airport.code,
                name: seg.destination.airport.name,
              },
            },
            totalSeatsAvailable: seg.totalSeatsAvailable,
          })) || [], // Default to empty array if no segments
      },
      return: {
        classCode: selectedAirfare?.return?.classCode || '',
        segments:
          selectedAirfare?.return?.segments?.map((seg: Segment) => ({
            airline: {
              code: seg.airline.code,
              name: seg.airline.name,
              description: seg.airline.description,
            },
            arrivalDate: seg.arrivalDate,
            blockStatus: seg.blockStatus as BlockStatusEnum,
            bookingType: seg.bookingType,
            cabinClassDescription: seg.cabinClassDescription,
            departureDate: seg.departureDate,
            departureTime: seg.departureTime,
            fareClassCode: seg.fareClassCode,
            id: seg.id,
            legNumber: seg.legNumber,
            number: seg.number,
            segmentNumber: seg.segmentNumber,
            origin: {
              airport: {
                code: seg.origin.airport.code,
                name: seg.origin.airport.name,
              },
            },
            blockType: seg.blockType ?? '',
            destination: {
              airport: {
                code: seg.destination.airport.code,
                name: seg.destination.airport.name,
              },
            },
            totalSeatsAvailable: seg.totalSeatsAvailable,
          })) || [], // Default to empty array if no segments
      },
    },
  };
};

export function getInsuranceRequestBody(
  firstCabinSelections: CabinSelectionsType,
  passengerType: string,
  discountCodesArray: Discount[],
  selectedAirfare: Airfare | undefined,
  destinationAirport: string,
  originAir: string,
  dateParams: string
) {
  const startDate: string | null | undefined = store?.getState()?.cruiseData?.departureDate;
  // TODO - Add logic for second cabin
  const mandatoryItemsFirstCabin =
    firstCabinSelections?.extraServices?.filter(
      (item: Item) => item.applicability === 'MANDATORY' && item.typeCode !== 'AIR'
    ) || [];

  const trainItemSelectionFirstCabin =
    firstCabinSelections?.extraServices?.filter(
      (item: Item) =>
        item.isIncludedInAllInclusiveCabinPrice && item.applicability === 'SUBSTITUTABLE'
    ) || [];

  const selectedItemsFirstCabin = firstCabinSelections?.selectedExtraServices || [];

  const itemsFirstCabin = [
    ...mandatoryItemsFirstCabin,
    ...selectedItemsFirstCabin,
    ...trainItemSelectionFirstCabin,
  ];

  const adultsDataFirstCabin: GuestData[] = firstCabinSelections?.passengersData
    ?.filter((passenger) => passenger?.cabinIndex === 0 && passenger?.type === 'adult')
    .map((adult, index) => ({
      ...adult,
      leadPassenger: index === 0,
    }));

  const childrenDetailsFirstCabin: GuestData[] = firstCabinSelections?.passengersData?.filter(
    (passenger) => passenger?.cabinIndex === 0 && passenger?.type !== 'adult'
  );

  const adults = [...adultsDataFirstCabin];
  const children = [...childrenDetailsFirstCabin];

  const air = createAirObject(dateParams, originAir, destinationAirport, selectedAirfare);

  const isServiceChargeAvailable = !!(
    firstCabinSelections?.selectedCategory?.serviceCharges?.[0] ||
    firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.serviceCharges?.[0]
  );

  const serviceCharges: ServiceCharge[] | undefined = isServiceChargeAvailable
    ? new Array(
        firstCabinSelections?.selectedCategory?.serviceCharges?.[0] ||
          firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory
            ?.serviceCharges?.[0]
      )
    : undefined;

  const insuranceRequest: InsuranceRequest = {
    passengerInformation: {
      ...(passengerType && { passengerType: passengerType }),
      adults: {
        total: adults.length,
        details: adults.map(
          (adult): PassengerDetail => ({
            age: adult.age,
            address: {
              countryCode: adult.countryOfResidence,
            },
          })
        ),
      },
      ...(children.length > 0 && {
        children: {
          total: children.length,
          details: children.map(
            (child): PassengerDetail => ({
              age: child.age,
              address: {
                countryCode: child.countryOfResidence,
              },
            })
          ),
        },
      }),
    },
    cruise: {
      priceCode:
        firstCabinSelections?.selectedCategory?.priceCode ||
        firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.priceCode,
      ...(discountCodesArray?.length && { discounts: discountCodesArray }),
      items: itemsFirstCabin.map((item: Item) => ({
        packageCode: item.packageCode,
        type: item.typeCode,
        code: item.code,
        startDate: item.date,
        endDateOffset: item.endDateOffset,
      })),
      serviceCharges: serviceCharges?.map((serviceCharge: ServiceCharge) => ({
        type: serviceCharge?.type,
        code: serviceCharge?.code,
        startDate: startDate,
      })) as ServiceCharge[],
      ...(air && {
        air,
      }),
    },
  };

  return insuranceRequest;
}

export function getPriceToBookRequestBody(
  passengerType: string,
  discountCodesArrayFirstCabin: Discount[],
  discountCodesArraySecondCabin: Discount[],
  firstCabinSelections: CabinSelectionsType,
  secondCabinSelections: CabinSelectionsType,
  eCoupon: ECouponType,
  occupancySecondCabinGuests: FirstCabinGuests & {
    requestConnectedCabin: boolean;
    needed: boolean;
  },
  selectedInsurances: InsuranceItem[],
  selectedAirfare: Airfare | undefined,
  destinationAirport: string,
  originAir: string,
  dateParams: string,
  paymentMethodOverride?: string
) {
  const startDate: string | null | undefined = store?.getState()?.cruiseData?.departureDate;
  const mandatoryItemsFirstCabin =
    firstCabinSelections?.extraServices?.filter(
      (item: Item) => item.applicability === 'MANDATORY' && item.typeCode !== 'AIR'
    ) || [];

  const trainItemSelectionFirstCabin =
    firstCabinSelections?.extraServices?.filter(
      (item: Item) =>
        item.isIncludedInAllInclusiveCabinPrice && item.applicability === 'SUBSTITUTABLE'
    ) || [];

  const mandatoryItemsSecondCabin =
    secondCabinSelections?.extraServices?.filter(
      (item: Item) => item.applicability === 'MANDATORY' && item.typeCode !== 'AIR'
    ) || [];
  const selectedItemsFirstCabin = firstCabinSelections?.selectedExtraServices || [];
  const selectedItemsSecondCabin = secondCabinSelections?.selectedExtraServices || [];

  const itemsFirstCabin = [
    ...mandatoryItemsFirstCabin,
    ...selectedItemsFirstCabin,
    ...trainItemSelectionFirstCabin,
  ];
  const itemsSecondCabin = [...mandatoryItemsSecondCabin, ...selectedItemsSecondCabin];

  const adultsDataFirstCabin: GuestData[] = firstCabinSelections?.passengersData
    ?.filter((passenger) => passenger?.cabinIndex === 0 && passenger?.type === 'adult')
    .map((adult, index) => ({
      ...adult,
      leadPassenger: index === 0,
    }));
  const adultsDataSecondCabin: GuestData[] = secondCabinSelections?.passengersData?.filter(
    (passenger) => passenger?.cabinIndex === 1 && passenger?.type === 'adult'
  );

  const childrenDetailsFirstCabin: GuestData[] = firstCabinSelections?.passengersData?.filter(
    (passenger) => passenger?.cabinIndex === 0 && passenger?.type !== 'adult'
  );
  const childrenDetailsSecondCabin: GuestData[] = secondCabinSelections?.passengersData?.filter(
    (passenger) => passenger?.cabinIndex === 1 && passenger?.type !== 'adult'
  );

  const isServiceChargeAvailable = !!(
    firstCabinSelections?.selectedCategory?.serviceCharges?.[0] ||
    firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.serviceCharges?.[0]
  );
  const adults = [...adultsDataFirstCabin, ...adultsDataSecondCabin];
  const children = [...childrenDetailsFirstCabin, ...childrenDetailsSecondCabin];

  const air = createAirObject(dateParams, originAir, destinationAirport, selectedAirfare);

  const serviceCharges: ServiceCharge[] | undefined = isServiceChargeAvailable
    ? new Array(
        firstCabinSelections?.selectedCategory?.serviceCharges?.[0] ||
          firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory
            ?.serviceCharges?.[0]
      )
    : undefined;

  const secondCabinServiceCharges: ServiceCharge[] | undefined = new Array(
    secondCabinSelections?.selectedCategory?.serviceCharges?.[0] ||
      secondCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.serviceCharges?.[0]
  );

  const state = store?.getState() as unknown as RootState | undefined;
  const activePayment = state?.bookingStepsStore?.paymentMethods?.find((el) => el.active);
  const selectedPaymentMethod =
    paymentMethodOverride ??
    activePayment?.method ??
    state?.payment?.selectedPayment?.paymentMethod;
  const paymentMethodApi = getPaymentMethodForPriceToBook(selectedPaymentMethod);

  const enableApiPaymentKlarna = isEnableApiPaymentKlarna();

  //MOCK
  //const enableApiPaymentKlarna = true;
  //const isKlarnaPaymentMethod =
  //  Array.isArray(paymentMethodApi) && paymentMethodApi[0] === 'KLARNA';

  const request: PriceToBookRequest = {
    ...(!eCoupon?.isMultiusage && { couponCode: eCoupon.code?.toUpperCase() }),
    returnPath:
      (isProduction() || isChain2() ? '' : window.location.origin) +
      `${window.location.pathname}-paymentredirection${window.location.search}`,
    ...(enableApiPaymentKlarna && { paymentMethods: paymentMethodApi }),
    /*...(enableApiPaymentKlarna &&
      isKlarnaPaymentMethod && {
        fakeKlarnaTestParam: 'FORCE_ERROR',
      }),*/
    passengerInformation: {
      ...(passengerType && { passengerType: passengerType }),
      adults: {
        total: adults.length,
        details: adults.map(
          (adult): AdultDetail => ({
            firstName: adult.firstName,
            lastName: adult.lastName,
            dateOfBirth: adult.dateOfBirth,
            age: adult.age,
            number: adult.number,
            leadPassenger: adult.leadPassenger,
            address: {
              countryCode: adult.countryOfResidence,
            },
            ...(enableApiPaymentKlarna && adult.email && { email: adult.email }),
            ...(enableApiPaymentKlarna &&
              adult.phone && {
                phone: adult.phone,
              }),
          })
        ),
      },
      ...(children.length > 0 && {
        children: {
          total: children.length,
          details: children.map((child) => ({
            firstName: child.firstName,
            lastName: child.lastName,
            dateOfBirth: child.dateOfBirth,
            age: child.age,
            number: child.number,
            address: {
              countryCode: child.countryOfResidence,
            },
          })),
        },
      }),
    },
    cabins: [
      {
        passengers: firstCabinSelections?.passengersData
          ?.map((pax) => pax?.number)
          .filter((num): num is number => num !== undefined),
        priceCode:
          firstCabinSelections?.selectedCategory?.priceCode ||
          firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.priceCode,
        categoryCode:
          firstCabinSelections?.selectedCategory?.categoryCode ||
          firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.categoryCode,
        items: itemsFirstCabin.map((item: Item) => ({
          packageCode: item.packageCode,
          type: item.typeCode,
          code: item.code,
          startDate: item.date,
          endDateOffset: item.endDateOffset,
        })),
        ...(discountCodesArrayFirstCabin?.length && { discounts: discountCodesArrayFirstCabin }),
        serviceCharges: serviceCharges?.map((serviceCharge: ServiceCharge) => ({
          type: serviceCharge?.type,
          code: serviceCharge?.code,
          startDate: startDate,
        })) as ServiceCharge[],
        insurances: selectedInsurances?.map((insurance: InsuranceItem) => ({
          code: insurance.code,
          supplierCode: insurance.supplierCode,
        })),
        ...(air && {
          air,
        }),
      },
      ...(occupancySecondCabinGuests.needed
        ? [
            {
              passengers: secondCabinSelections?.passengersData
                .map((pax) => pax.number)
                .filter((num): num is number => num !== undefined),
              priceCode:
                secondCabinSelections?.selectedCategory?.priceCode ||
                secondCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.priceCode,
              categoryCode:
                secondCabinSelections?.selectedCategory?.categoryCode ||
                secondCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory
                  ?.categoryCode,
              items: itemsSecondCabin.map((item: Item) => ({
                packageCode: item.packageCode,
                type: item.typeCode,
                code: item.code,
                startDate: item.date,
                endDateOffset: item.endDateOffset,
              })),
              ...(discountCodesArraySecondCabin?.length && {
                discounts: discountCodesArraySecondCabin,
              }),
              serviceCharges: secondCabinServiceCharges?.map((serviceCharge: ServiceCharge) => ({
                type: serviceCharge?.type,
                code: serviceCharge?.code,
                startDate: startDate,
              })) as ServiceCharge[],
              insurances: [],
              ...(air && {
                air,
              }),
            },
          ]
        : []),
    ],
  };
  return request;
}

export const createBookingRequestBody = (
  passengerType: string,
  discountCodesArrayFirstCabin: Discount[],
  discountCodesArraySecondCabin: Discount[],
  firstCabinSelections: CabinSelectionsType,
  secondCabinSelections: CabinSelectionsType,
  firstCabinNumber: string,
  secondCabinNumber: string,
  cruiseId: string,
  bookingType: string,
  couponCode: string,
  occupancySecondCabinGuests: FirstCabinGuests & {
    requestConnectedCabin: boolean;
    needed: boolean;
  },
  selectedDiningOption: string,
  selectedInsurances: InsuranceItem[],
  selectedAirfare: Airfare | undefined,
  destinationAirport: string,
  originAir: string,
  dateParams: string,
  webform?: Webform | null,
  emergency?: EmergencyContact,
  transactionId?: string,
  holdOptionPhone?: string,
  holdOptionPrefix?: string
) => {
  const state = store?.getState();

  const startDate: string | null | undefined = store?.getState()?.cruiseData?.departureDate;

  const transId =
    transactionId ||
    (bookingType === 'FULL'
      ? state?.bookingStepsStore?.prices?.booking?.total?.transactionId
      : bookingType === 'DEPOSIT'
      ? state?.bookingStepsStore?.prices?.booking?.deposit?.transactionId
      : '');

  const mandatoryItemsFirstCabin =
    firstCabinSelections?.extraServices?.filter(
      (item: Item) => item.applicability === 'MANDATORY' && item.typeCode !== 'AIR'
    ) || [];
  const trainItemSelectionFirstCabin =
    firstCabinSelections?.extraServices?.filter(
      (item: Item) =>
        item.isIncludedInAllInclusiveCabinPrice && item.applicability === 'SUBSTITUTABLE'
    ) || [];

  const mandatoryItemsSecondCabin =
    secondCabinSelections?.extraServices?.filter(
      (item: Item) => item.applicability === 'MANDATORY' && item.typeCode !== 'AIR'
    ) || [];
  const selectedItemsFirstCabin = firstCabinSelections?.selectedExtraServices || [];
  const selectedItemsSecondCabin = secondCabinSelections?.selectedExtraServices || [];

  const itemsFirstCabin = [
    ...mandatoryItemsFirstCabin,
    ...selectedItemsFirstCabin,
    ...trainItemSelectionFirstCabin,
  ];
  const itemsSecondCabin = [...mandatoryItemsSecondCabin, ...selectedItemsSecondCabin];

  const adultsDataFirstCabin: GuestData[] = firstCabinSelections?.passengersData
    ?.filter((passenger) => passenger?.cabinIndex === 0 && passenger?.type === 'adult')
    .map((adult, index) => ({
      ...adult,
      leadPassenger: index === 0,
    }));
  const adultsDataSecondCabin: GuestData[] = secondCabinSelections?.passengersData?.filter(
    (passenger) => passenger?.cabinIndex === 1 && passenger?.type === 'adult'
  );

  const childrenDetailsFirstCabin: GuestData[] = firstCabinSelections?.passengersData?.filter(
    (passenger) => passenger?.cabinIndex === 0 && passenger?.type !== 'adult'
  );
  const childrenDetailsSecondCabin: GuestData[] = secondCabinSelections?.passengersData?.filter(
    (passenger) => passenger?.cabinIndex === 1 && passenger?.type !== 'adult'
  );

  const adults = [...adultsDataFirstCabin, ...adultsDataSecondCabin];
  const children = [...childrenDetailsFirstCabin, ...childrenDetailsSecondCabin];

  const voyagersMembersInfo = [
    ...firstCabinSelections.voyagersMembersInfo,
    ...secondCabinSelections.voyagersMembersInfo,
  ];

  const air = createAirObject(dateParams, originAir, destinationAirport, selectedAirfare);

  const serviceCharges: ServiceCharge[] | undefined =
    firstCabinSelections?.selectedCategory?.serviceCharges?.slice(0, 1) ||
    firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.serviceCharges?.slice(
      0,
      1
    );

  const secondCabinServiceCharges: ServiceCharge[] | undefined =
    secondCabinSelections?.selectedCategory?.serviceCharges?.slice(0, 1) ||
    secondCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.serviceCharges?.slice(
      0,
      1
    );

  const request: BookingsRequest = {
    cruiseId,
    passengerInformation: {
      passengerType: passengerType || '',
      adults: {
        total: adults.length,
        details: adults.map((adult, guestIndex): PassengerDetails => {
          const voyagerInfo = voyagersMembersInfo.find(
            (voyagerData) => voyagerData.guestIndex === guestIndex
          );
          return {
            ...(adult.documentType && {
              identityDocument: {
                type: adult.documentType || '',
                number: adult.documentNumber || '',
                expiryDate: adult.dateOfExpire || '',
                ...(adult.documentType === 'PASSPORT' &&
                  adult.passportDateOfIssue && { issueDate: adult.passportDateOfIssue || '' }),
                ...(adult.documentType === 'PASSPORT' &&
                  adult.countryOfIssue && { issuedAt: adult.countryOfIssue || '' }),
              },
            }),
            dateOfBirth: adult.dateOfBirth || '',
            address: {
              country: adult.countryOfResidence || '',
            },
            lastName: adult.lastName || '',
            number: adult.number || 0,
            age: adult.age || 18,
            firstName: adult.firstName || '',
            ...(voyagerInfo && {
              membership: {
                number: voyagerInfo.memberInfo?.membership?.number.toString() || '',
              },
            }),
            ...(adult.email &&
              adult.email.trim().length > 0 && {
                email: adult.email,
              }),
            ...(holdOptionPhone &&
            adult.phone &&
            adult.phone.trim().length > 0 &&
            holdOptionPhone.trim().length > 0
              ? {
                  phone: holdOptionPhone.replace(/-/g, ''),
                }
              : adult.phone &&
                adult.phone.trim().length > 0 && {
                  phone: adult.phone.replace(/-/g, ''),
                }),
            ...(holdOptionPrefix &&
            adult.prefix &&
            adult.prefix.trim().length > 0 &&
            holdOptionPrefix.trim().length > 0
              ? {
                  phonePrefix: '+' + holdOptionPrefix,
                }
              : adult.prefix &&
                adult.prefix.trim().length > 0 && {
                  phonePrefix: '+' + adult.prefix,
                }),
            gender: adult.gender || '',
            ...(adult.nationality &&
              adult.nationality.trim().length > 0 && {
                nationality: adult.nationality || '',
              }),
            leadPassenger: adult.leadPassenger || false,
          };
        }),
      },
      ...(children.length > 0 && {
        children: {
          total: children.length || 0,
          details: children.map((child) => ({
            ...(child.documentType && {
              identityDocument: {
                type: child.documentType || '',
                number: child.documentNumber || '',
                expiryDate: child.dateOfExpire || '',
                ...(child.documentType === 'PASSPORT' &&
                  child.passportDateOfIssue && { issueDate: child.passportDateOfIssue || '' }),
                ...(child.documentType === 'PASSPORT' &&
                  child.countryOfIssue && { issuedAt: child.countryOfIssue || '' }),
              },
            }),
            dateOfBirth: child.dateOfBirth || '',
            address: {
              country: child.countryOfResidence || '',
            },
            lastName: child.lastName || '',
            firstName: child.firstName || '',
            age: child.age || 18,
            number: child.number || 0,
            ...(child.email && {
              email: child.email,
            }),
            ...(child.phone && {
              phone: child.phone.replace(/-/g, ''),
            }),
            ...(child.prefix && {
              phonePrefix: '+' + child.prefix,
            }),
            gender: child.gender || '',
            ...(child.nationality && {
              nationality: child.nationality || '',
            }),
          })),
        },
      }),
      ...(emergency &&
        emergency.firstName.trim().length > 0 &&
        emergency.phone.trim().length > 0 &&
        emergency.prefix.trim().length > 0 && {
          emergencyContact: {
            name: emergency.firstName,
            phone: emergency.phone.replace(/-/g, ''),
            phonePrefix: '+' + emergency.prefix,
          },
        }),
    },
    bookingType,
    ...(transId && { transactionId: transId }),
    cabins: [
      {
        categoryCode:
          firstCabinSelections?.selectedCategory?.categoryCode ||
          firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.categoryCode ||
          '',
        passengers: firstCabinSelections?.passengersData
          ?.map((pax) => pax?.number)
          .filter((num): num is number => num !== undefined),
        number:
          firstCabinSelections?.selectedCabinPreference === 'GUARANTEED'
            ? 'G00000'
            : firstCabinNumber.replace('cabin', ''),
        priceCode:
          firstCabinSelections?.selectedCategory?.priceCode ||
          firstCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.priceCode ||
          '',
        ...(discountCodesArrayFirstCabin?.length && { discounts: discountCodesArrayFirstCabin }),
        diningOption: selectedDiningOption ? { code: selectedDiningOption } : {},
        items: itemsFirstCabin.map((item: Item) => ({
          packageCode: item.packageCode,
          type: item.typeCode,
          code: item.code,
          startDate: item.date,
          endDateOffset: item.endDateOffset,
        })),
        serviceCharges: serviceCharges?.map((serviceCharge: ServiceCharge) => ({
          type: serviceCharge?.type,
          code: serviceCharge?.code,
          startDate: startDate,
        })) as ServiceCharge[],
        insurances: state?.bookingStepsStore?.selectedInsurance?.map(
          (insurance: InsuranceItem) => ({
            code: insurance.code,
            supplierCode: insurance.supplierCode,
          })
        ),
        ...(air && {
          air,
        }),
      },
      ...(occupancySecondCabinGuests.needed
        ? [
            {
              categoryCode:
                secondCabinSelections?.selectedCategory?.categoryCode ||
                secondCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory
                  ?.categoryCode ||
                '',
              passengers: secondCabinSelections?.passengersData
                .map((pax) => pax.number)
                .filter((num): num is number => num !== undefined),
              number:
                secondCabinSelections?.selectedCabinPreference === 'GUARANTEED'
                  ? 'G00000'
                  : secondCabinNumber.replace('cabin', ''),
              priceCode:
                secondCabinSelections?.selectedCategory?.priceCode ||
                secondCabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory
                  ?.priceCode ||
                '',
              ...(discountCodesArraySecondCabin?.length && {
                discounts: discountCodesArraySecondCabin,
              }),
              diningOption: {},
              items: itemsSecondCabin.map((item: Item) => ({
                packageCode: item.packageCode,
                type: item.typeCode,
                code: item.code,
                startDate: item.date,
                endDateOffset: item.endDateOffset,
              })),
              serviceCharges: secondCabinServiceCharges?.map((serviceCharge: ServiceCharge) => ({
                type: serviceCharge?.type,
                code: serviceCharge?.code,
                startDate: startDate,
              })) as ServiceCharge[],
              insurances: [],
              ...(air && {
                air,
              }),
            },
          ]
        : []),
    ],
    ...(couponCode && { couponCode: couponCode?.toUpperCase() }),
    ...(bookingType === 'HOLD' && {
      webform,
    }),
  };

  return request;
};

export const getAirfaresRequest = (
  occupancyFirstCabinGuests: FirstCabinGuests,
  occupancySecondCabinGuests: SecondCabinGuests,
  depAirportCode: string,
  destination: string,
  departureDate: string,
  returnDate: string,
  priceCode: string,
  cabinCategory: string,
  classCode: ClassCodeFlightEnum,
  passengersData: GuestData[],
  cabinIndex: number,
  adultsOccupancy: number,
  childrenOccupancy: number,
  infantOccupancy: number,
  promotionAges: {
    adultAge: number | undefined;
    minorAge: number | undefined;
    infantAge: number | undefined;
  } | null
) => {
  const adults =
    occupancyFirstCabinGuests.adults +
    (occupancySecondCabinGuests.needed ? occupancySecondCabinGuests.adults : 0);

  const { minorsSaved, infantsSaved } = getPassengersData(
    passengersData,
    cabinIndex,
    adultsOccupancy || 2,
    childrenOccupancy || 0,
    infantOccupancy || 0,
    promotionAges
  );

  const childrenArray: ChildrenDetails[] = [...(minorsSaved || []), ...(infantsSaved || [])]?.length
    ? [...(minorsSaved || []), ...(infantsSaved || [])]?.map((child) => ({
        age: child.age,
      }))
    : [
        ...Array(childrenOccupancy).fill({ age: promotionAges?.minorAge || 10 }),
        ...Array(infantOccupancy).fill({ age: promotionAges?.infantAge || 1 }),
      ];

  const airfairsRequest: AirfareRequest = {
    origin: depAirportCode,
    destination: destination,
    departureDate,
    returnDate,
    originType: OriginTypeFlightEnum.AIRPORT,
    priceCode,
    cabin: {
      category: cabinCategory,
    },
    classCode, // [ BUSINESS_CLASS, FIRST_CLASS, PREMIUM_CLASS, ECONOMY ]
    airlineCode: '*',
    passengerInformation: {
      adults: {
        total: adults,
      },
      children: {
        total: childrenArray?.length || 0,
        details: childrenArray ?? [],
      },
    },
  };

  return airfairsRequest;
};

export const getDiscountsRequest = (
  loyaltyNumber: string,
  dateOfBirthValue: string,
  isDealValue: boolean,
  name?: string,
  surname?: string
) => {
  return {
    loyaltyCard: {
      number: loyaltyNumber,
      dateOfBirth: dateOfBirthValue,
      ...(name && { name: name }),
      ...(surname && { surname: surname }),
    },
    isDeal: isDealValue,
  };
};

export function compareDiscountsBody(
  cabinSelections: CabinSelectionsType,
  passengerType: string,
  discountCodesArray: Discount[],
  isFlightIncluded: boolean,
  additonalDiscountCodesArray: AdditionalDiscount[],
  loyaltyCard?: LoyaltyCard
) {
  const adults: GuestData[] = cabinSelections?.passengersData
    ?.filter((passenger) => passenger?.cabinIndex === 0 && passenger?.type === 'adult')
    .map((adult, index) => ({
      ...adult,
      leadPassenger: index === 0,
    }));

  const children: GuestData[] = cabinSelections?.passengersData?.filter(
    (passenger) => passenger?.cabinIndex === 0 && passenger?.type !== 'adult'
  );

  const compareDiscountsRequest: CompareDiscountsRequest = {
    priceCode:
      cabinSelections?.selectedCategory?.priceCode ||
      cabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.priceCode,
    categoryCode:
      cabinSelections?.selectedCategory?.categoryCode ||
      cabinSelections?.selectedCabinPreferenceObj?.lowestPricedCategory?.categoryCode,
    isFlightIncluded,
    passengerInformation: {
      passengerType: passengerType,
      adults: {
        total: adults.length,
        details: adults.map(
          (adult): AdultDetail => ({
            age: adult.age,
          })
        ),
      },
      ...(children.length > 0 && {
        children: {
          total: children.length,
          details: children.map((child) => ({
            age: child.age,
          })),
        },
      }),
    },
    ...(discountCodesArray?.length && { discounts: discountCodesArray }),
    ...(additonalDiscountCodesArray?.length && {
      additionalDiscounts: additonalDiscountCodesArray,
    }),
    ...(loyaltyCard?.number && { loyaltyCard: loyaltyCard }),
  };

  return compareDiscountsRequest;
}

export const sendNotificationsReqBody = (
  passengerType: string,
  campaignId: string,
  firstCabinSelections: CabinSelectionsType,
  secondCabinSelections: CabinSelectionsType,
  transactionAmount: string,
  submissionDateTime: number
) => {
  const adultsDataFirstCabin: GuestData[] = firstCabinSelections?.passengersData
    ?.filter((passenger) => passenger?.cabinIndex === 0 && passenger?.type === 'adult')
    .map((adult, index) => ({
      ...adult,
      leadPassenger: index === 0,
    }));
  const adultsDataSecondCabin: GuestData[] = secondCabinSelections?.passengersData?.filter(
    (passenger) => passenger?.cabinIndex === 1 && passenger?.type === 'adult'
  );

  const childrenDetailsFirstCabin: GuestData[] = firstCabinSelections?.passengersData?.filter(
    (passenger) => passenger?.cabinIndex === 0 && passenger?.type !== 'adult'
  );
  const childrenDetailsSecondCabin: GuestData[] = secondCabinSelections?.passengersData?.filter(
    (passenger) => passenger?.cabinIndex === 1 && passenger?.type !== 'adult'
  );

  const adults = [...adultsDataFirstCabin, ...adultsDataSecondCabin];
  const children = [...childrenDetailsFirstCabin, ...childrenDetailsSecondCabin];

  const voyagersMembersInfo = [
    ...firstCabinSelections.voyagersMembersInfo,
    ...secondCabinSelections.voyagersMembersInfo,
  ];

  const request: NotificationsRequest = {
    campaignId,
    passengerInformation: {
      passengerType: passengerType || '',
      adults: {
        total: adults.length,
        details: adults.map((adult, guestIndex): PassengerDetails => {
          const voyagerInfo = voyagersMembersInfo.find(
            (voyagerData) => voyagerData.guestIndex === guestIndex
          );
          return {
            ...(adult.documentType && {
              identityDocument: {
                type: adult.documentType || '',
                number: adult.documentNumber || '',
                expiryDate: adult.dateOfExpire || '',
                ...(adult.documentType === 'PASSPORT' &&
                  adult.passportDateOfIssue && { issueDate: adult.passportDateOfIssue || '' }),
                ...(adult.documentType === 'PASSPORT' &&
                  adult.countryOfIssue && { issuedAt: adult.countryOfIssue || '' }),
              },
            }),
            dateOfBirth: adult.dateOfBirth || '',
            address: {
              country: adult.countryOfResidence || '',
            },
            lastName: adult.lastName || '',
            number: adult.number || 0,
            age: adult.age || 18,
            firstName: adult.firstName || '',
            ...(voyagerInfo && {
              membership: {
                number: voyagerInfo.memberInfo?.membership?.number.toString() || '',
              },
            }),
            ...(adult.email && { email: adult.email }),
            ...(adult.phone &&
              adult.phone.trim().length > 0 && {
                phone: adult.phone.replace(/-/g, ''),
              }),
            ...(adult.prefix &&
              adult.prefix.trim().length > 0 && {
                phonePrefix: '+' + adult.prefix,
              }),
            ...(adult.gender && { gender: adult.gender }),
            ...(adult.nationality && { nationality: adult.nationality }),
            leadPassenger: adult.leadPassenger || false,
          };
        }),
      },
      ...(children.length > 0 && {
        children: {
          total: children.length || 0,
          details: children.map((child) => ({
            ...(child.documentType && {
              identityDocument: {
                type: child.documentType || '',
                number: child.documentNumber || '',
                expiryDate: child.dateOfExpire || '',
                ...(child.documentType === 'PASSPORT' &&
                  child.passportDateOfIssue && { issueDate: child.passportDateOfIssue || '' }),
                ...(child.documentType === 'PASSPORT' &&
                  child.countryOfIssue && { issuedAt: child.countryOfIssue || '' }),
              },
            }),
            dateOfBirth: child.dateOfBirth || '',
            ...(child.countryOfResidence && {
              address: {
                country: child.countryOfResidence || '',
              },
            }),
            lastName: child.lastName || '',
            firstName: child.firstName || '',
            age: child.age || 18,
            number: child.number || 0,
            ...(child.email && { email: child.email }),
            ...(child.phone && {
              phone: child.phone.replace(/-/g, ''),
            }),
            ...(child.prefix && {
              phonePrefix: '+' + child.prefix,
            }),
            ...(child.gender && { gender: child.gender }),
            ...(child.nationality && { nationality: child.nationality }),
            leadPassenger: child.leadPassenger,
          })),
        },
      }),
    },
    transactionAmount,
    submissionDateTime,
  };
  return request;
};

export const generatePaymentOptions = (
  totalAmount: string,
  allowedCardNetworks: Array<string>,
  useGooglePay: boolean,
  useApplePay: boolean,
  currency: string,
  countryCode: string,
  refNo: string,
  merchantId: string
) => {
  return {
    merchantId,
    merchantName: getPaymentButtonMerchantName(),
    options: {
      allowedCardNetworks,
      useGooglePay,
      useApplePay,
    },
    payment: {
      details: {
        total: {
          label: getPaymentButtonAmountPaymentLabel(),
          amount: { value: totalAmount, currency: currency },
        },
      },
      options: {
        requestPayerEmail: false,
        requestPayerName: false,
        requestPayerPhone: false,
      },
      transaction: {
        countryCode: countryCode,
        refno: refNo,
        authenticationOnly: true,
      },
    },
  };
};

export const isNotEmpty = (value: string | undefined) => {
  return value?.trim() !== '';
};

// Function to capitalize the first letter of each word and convert the rest to lowercase
export const capitalizeFirstLetterAndLowerCaseRest = (string: string) => {
  return string
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const getAge = (dateOfBirth: Date, embarkationDate: Date) => {
  // Calculate the year difference
  let age = embarkationDate.getFullYear() - dateOfBirth.getFullYear();

  // Adjust if the birthday hasn't occurred yet in the future year
  if (
    embarkationDate.getMonth() < dateOfBirth.getMonth() ||
    (embarkationDate.getMonth() === dateOfBirth.getMonth() &&
      embarkationDate.getDate() < dateOfBirth.getDate())
  ) {
    age--;
  }

  return age;
};

export const convertPathToDotNotation = (data: any, path: string) => {
  if (data && path) {
    // Convert the path to dot notation (replace square brackets with dot notation)
    const normalizedPath = path.replace(/\[(\d+)]/g, '.$1');

    // Split the normalized path into parts
    const keys = normalizedPath.split('.');

    // Start from the top of the data
    let currentValue = data; //.errors;

    // Traverse the JSON structure
    for (const key of keys) {
      const finalKey = isNaN(Number(key)) ? key : Number(key);
      currentValue = currentValue?.[finalKey]; // Traverse to the next level
    }

    // currentValue now contains the firstName object
    return currentValue;
  }
  return null;
};

export const createObjectFromDotNotation = (notationString: string, value: string) => {
  const result: Record<string, any> = {};
  const keys = notationString.split(/[.[\]]/).filter(Boolean); // Split by "." and "[" or "]"

  let current = result;

  keys.forEach((key: string, index) => {
    const finalKey = isNaN(Number(key)) ? key : Number(key);

    if (index === keys.length - 1) {
      // If it's the last key, assign the value
      current[finalKey] = value;
    } else {
      // Create an empty object or array if not already existing
      current[finalKey] = current[finalKey] || (isNaN(Number(keys[index + 1])) ? {} : []);
    }

    current = current[finalKey];
  });

  return result;
};

export function getQueryParamIgnoreCase(queryString: string, param: string) {
  // remove the '?' from the query string and split the string into an array of key-value pairs
  const params = queryString.substring(1).split('&');
  param = param.toLowerCase();

  for (const paramPair of params) {
    const [key, value] = paramPair.split('=');
    if (key && key.toLowerCase() === param) {
      return decodeURIComponent(value);
    }
  }

  return null;
}

export function temporaryIsScene7Image(imageURL: string) {
  return imageURL?.startsWith('http');
}

export const getErrorMessage = (data: unknown): string => {
  let message: string;

  if (data && typeof data === 'object' && 'error' in data) {
    const errorData = (data as { error: unknown }).error;
    message = typeof errorData === 'string' ? errorData : String(errorData);
  } else if (data instanceof Error) {
    message = data.message;
  } else if (data && typeof data === 'object' && 'message' in data) {
    message = String((data as { message: unknown }).message);
  } else if (typeof data === 'string') {
    message = data;
  } else {
    message = 'Something went wrong';
  }

  return message;
};

/**
 * Checks if the itinerary data contains a discount code that matches the applied promotion.
 *
 * @param {AlgoliaSearchResponse['hits']} itineraryData - The itinerary data containing discount codes.
 * @returns The discount code object if a matching promotion is found, otherwise null.
 */
export function pricesWithPromo(itineraryData: Hit) {
  const paxTypeApplied: FilterOptionSliceType[] | undefined =
    store?.getState()?.genericFilter?.promotion;
  // check if itineraryData.discountCodes array contains (inside paxType field) one of the paxType applied
  // function return the discountCode object inside itineraryData if the related pax type matches with on of the pax type applied
  // otherwise return null
  if (paxTypeApplied && paxTypeApplied.length > 0) {
    const discountCode = itineraryData?.discountCodes?.find((discountCode: DiscountType) => {
      return paxTypeApplied.some((paxType) => {
        return discountCode.paxType === paxType.value;
      });
    });
    if (discountCode) {
      return discountCode;
    } else {
      return null;
    }
  } else {
    return null;
  }
}

export function calculateTotalPriceObj(itineraryData: Hit): {
  prices: { adultPrice: number; cabinPrice: number };
  strikethroughPrices: { adultPrice: number; cabinPrice: number };
} {
  const isDeal = getDealsFilters().length > 0;
  const paxTypeApplied: FilterOptionSliceType[] | undefined =
    store?.getState()?.genericFilter?.promotion;

  const strikethroughDiscountCodesConfiguration = getStrikethroughDiscountCodes();

  const priceBeforeDiscount = itineraryData?.prices;
  const priceTypeBasedStrikethrough = itineraryData?.prices?.strikethroughPrice;
  const priceTypeBasedStrikethroughPages =
    itineraryData?.prices?.strikethroughPrice?.strikethroughPages;
  const priceAfterDiscount = itineraryData?.pricesAfterDiscount;
  const discountCodeStrikethrough = itineraryData?.pricesAfterDiscount?.strikethroughPrice;
  const appliedDiscountCode =
    itineraryData?.pricesAfterDiscount?.strikethroughPrice?.appliedDiscountCode;

  const discountCode = pricesWithPromo(itineraryData);

  if (
    paxTypeApplied.length === 0 &&
    (!appliedDiscountCode || appliedDiscountCode === '') &&
    !discountCode
  ) {
    return {
      prices: {
        adultPrice: priceBeforeDiscount.adultPrice,
        cabinPrice: priceBeforeDiscount.cabinPrice,
      },
      strikethroughPrices:
        priceTypeBasedStrikethroughPages &&
        ((isDeal && priceTypeBasedStrikethroughPages.includes('deals')) ||
          (!isDeal && priceTypeBasedStrikethroughPages.includes('search')))
          ? {
              adultPrice: priceTypeBasedStrikethrough.adultPrice,
              cabinPrice: priceTypeBasedStrikethrough.cabinPrice,
            }
          : { adultPrice: 0, cabinPrice: 0 },
    };
  }

  if (discountCode) {
    const actualPrice = isDeal
      ? {
          price: { adultPrice: discountCode.adultPrice, cabinPrice: discountCode.cabinPrice },
          strikethrough: {
            adultPrice: priceBeforeDiscount.adultPrice,
            cabinPrice: priceBeforeDiscount.cabinPrice,
          },
          discountCode: discountCode.codeOfDiscount,
        }
      : discountCode.adultPrice < priceAfterDiscount.adultPrice ||
        (discountCode.adultPrice === priceAfterDiscount.adultPrice &&
          discountCode.cabinPrice < priceAfterDiscount.cabinPrice)
      ? {
          price: { adultPrice: discountCode.adultPrice, cabinPrice: discountCode.cabinPrice },
          strikethrough: {
            adultPrice: priceBeforeDiscount.adultPrice,
            cabinPrice: priceBeforeDiscount.cabinPrice,
          },
          discountCode: discountCode.codeOfDiscount,
        }
      : {
          price: {
            adultPrice: priceAfterDiscount.adultPrice,
            cabinPrice: priceAfterDiscount.cabinPrice,
          },
          strikethrough: {
            adultPrice: discountCodeStrikethrough.adultPrice,
            cabinPrice: discountCodeStrikethrough.cabinPrice,
          },
          discountCode: appliedDiscountCode,
        };
    const matchingConfig = strikethroughDiscountCodesConfiguration?.find(
      (config) => config.discountCode === actualPrice.discountCode
    );

    if (matchingConfig?.enableStrikethroughCombination) {
      return { prices: actualPrice.price, strikethroughPrices: actualPrice.strikethrough };
    }
    return {
      prices: actualPrice.price,
      strikethroughPrices: { adultPrice: 0, cabinPrice: 0 },
    };
  }

  return {
    prices: {
      adultPrice: priceAfterDiscount.adultPrice,
      cabinPrice: priceAfterDiscount.cabinPrice,
    },
    strikethroughPrices: {
      adultPrice: discountCodeStrikethrough.adultPrice,
      cabinPrice: discountCodeStrikethrough.cabinPrice,
    },
  };
}

/**
 * Capitalizes the first letter of the given string.
 *
 * @param {string} string - The string to capitalize.
 * @returns The string with the first letter capitalized.
 */
export function capitalizeFLetter(string: string) {
  return string[0].toUpperCase() + string.slice(1);
}

/**
 * Returns the appropriate image source URL based on the screen size.
 *
 * @param {ImagePath | undefined} mobileImage - The image path object for mobile devices.
 * @param {ImagePath | undefined} desktopImage - The image path object for desktop devices.
 * @returns The URL of the image to be used based on the screen size.
 */
export function getImageSrc(
  mobileImage: ImagePath | undefined,
  desktopImage: ImagePath | undefined
) {
  const { width } = useWindowSize();
  const isMobile = width < breakpoints.md;
  // if _dmS7Url is present in image then return that image otherwise _path
  if (isMobile) {
    return mobileImage?._dmS7Url || mobileImage?._path;
  } else {
    return desktopImage?._dmS7Url || desktopImage?._path;
  }
}

export function isMobileDevice() {
  const { width } = useWindowSize();
  return width < breakpoints.md;
}

export function isDesktopDevice() {
  const { width } = useWindowSize();
  return width >= breakpoints.lg;
}

export function isTabletDevice() {
  const { width } = useWindowSize();
  return width >= breakpoints.md && width < breakpoints.lg;
}

/**
 * Returns attribute value from dom.
 *
 * @param {string | undefined} nameOfAttribute - The name of attribute.
 * @returns value - The the value of attribute ore undefined if not found.
 */
export function getAttributeValue(nameOfAttribute: string) {
  const element = document.querySelector('[' + nameOfAttribute + ']');
  return element ? element.getAttribute(nameOfAttribute) : undefined;
}

export function isTemplateClassPresent(nameOfClass: string) {
  return document.body.classList.contains(nameOfClass);
}

/**
 * Checks if the current site is multilingual based on the presence
 * of the language navigation component in the DOM.
 */
function isMultilangSite(): boolean {
  return !!document.querySelector('.cmp-languagenavigation');
}

/**
 * Rewrites internal AEM links from /content/b2c/... into correct
 * public URLs depending on whether the site is multilingual.
 *
 * - Multilang site → keep language in the path
 * - Single-lang site → remove language (keep only path after it)
 *
 * Examples:
 *   Multilang:    /content/b2c/ch/fr/page.html → /fr/page
 *   Single-lang:  /content/b2c/at/de/page.html → /page
 *
 * @param url The original AEM internal URL
 * @returns The cleaned, externalized path
 */
export function replaceInternalLink(url: string | null): string {
  if (!url) return '';

  const isMulti = isMultilangSite();

  if (isMulti) {
    // Keep the language segment → remove only the country
    // /content/b2c/<country>/<lang>/... → /<lang>/...
    return url.replace(/\/content\/b2c\/[^/]+\/([^/]+)(\/[^"']+?)(\.html)?(?=["']|$)/g, '/$1$2');
  } else {
    // Remove both country and language segments
    // /content/b2c/<country>/<lang>/... → /...
    return url.replace(/\/content\/b2c\/[^/]+\/[^/]+(\/[^"']+?)(\.html)?(?=["']|$)/g, '$1');
  }
}

export function addDaysAtSea<T extends ItineraryDetailsData | ItineraryResponse>(
  itineraryData: T,
  dayAtSeaLabel: string
): T extends ItineraryDetailsData
  ? { itineraryData: ItineraryDetailsData; days: Day[] }
  : { itineraryData: ItineraryResponse; days: CruiseDay[] } {
  const timeFormat = getTimeFormat();
  const updatedDays = [];
  const days = [...itineraryData.days];

  // Loop through the days and add days at sea between the ports if there are any
  for (let i = 0; i < days.length; i++) {
    const currentDay = { ...days[i] }; // Create a shallow copy of the current day
    const currentDayJs = dayjs(currentDay.date);

    updatedDays.push(currentDay);

    // Format arrivalTime and departureTime if they exist
    if (currentDay.arrivalTime) {
      currentDay.arrivalTime = dayjs(currentDay.arrivalTime, 'HH:mm:ss').format(timeFormat);
    }
    if (currentDay.departureTime) {
      currentDay.departureTime = dayjs(currentDay.departureTime, 'HH:mm:ss').format(timeFormat);
    }

    // Check if there is a next day
    if (i + 1 < days.length) {
      const nextDay = dayjs(days[i + 1].date);
      const differenceInDays = nextDay.diff(currentDayJs, 'day');

      // If there is a difference in days, add days at sea
      for (let j = 1; j < differenceInDays; j++) {
        const dayAtSeaDate = currentDayJs.add(j, 'day');
        updatedDays.push({
          date: dayAtSeaDate.format('YYYY-MM-DD'),
          dayOfWeek: dayAtSeaDate.format('dddd').toUpperCase(),
          port: { code: 'SEA', name: dayAtSeaLabel }, // No port for days at sea
          dayAtSea: true,
        });
      }
    }
  }

  return { itineraryData, days: updatedDays } as any;
}

/**
 * From mulesoft, the port description is encoded in HTML entities, this function decodes them
 */
export function decodeHTMLEntities(text: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(text, 'text/html');
  return doc.documentElement.textContent ?? '';
}

export function cleanHtml(text: string): string {
  let cleanedHtml = decodeHTMLEntities(text);
  cleanedHtml = replaceInternalLink(cleanedHtml);
  cleanedHtml = cleanedHtml.replace(/(?:^|[\s\u00A0])·\s*/g, '<br><br>• ');
  cleanedHtml = cleanedHtml.replace(/\s*style="[^"]*"/gi, '');
  cleanedHtml = cleanedHtml.replace(/<h2>\s*(?:&nbsp;)?\s*<\/h2>/gi, '');
  return cleanedHtml ?? '';
}

export const splitNightsRangeOnFilters = (filterValue: string) => {
  const optionValueSplitted = filterValue.split('-');
  const start = parseInt(optionValueSplitted[0]);
  const end = parseInt(optionValueSplitted[1]);
  let newValue = '';

  for (let i = start; i <= end; i++) {
    newValue = newValue.concat(i.toString());

    if (i < end) {
      newValue = newValue.concat(',');
    }
  }
  return newValue;
};

// ===== Support port function =====
export function getPortsFilterFromItineraries(
  itineraryResults: SearchResponse,
  destinationFilterIsActive: boolean
): string {
  if (!destinationFilterIsActive || !itineraryResults.facets) return '';
  const portOfCalls = itineraryResults.facets?.['portOfCalls'] || {};
  const embkPortKeys = itineraryResults.facets?.['embkPort.key'] || {};
  const allPorts = { ...portOfCalls, ...embkPortKeys };
  const allPortCodes = Object.keys(allPorts);
  if (allPortCodes.length > 0) {
    return `(${allPortCodes.map((code) => `portCode:"${code}"`).join(' OR ')})`;
  }
  return '';
}

export function combineFilters(mainFilters: string, portsFilter: string): string {
  if (mainFilters.trim() && portsFilter.trim()) {
    return `${mainFilters} AND ${portsFilter}`;
  }
  return mainFilters.trim() || portsFilter.trim();
}

export function buildItineraryFiltersFromExcursionPorts(portCodesObj: Record<string, any>): string {
  const portCodes = Object.keys(portCodesObj);
  if (portCodes.length === 0) return '';
  const portOfCallsFilter = portCodes.map((code) => `portOfCalls:"${code}"`).join(' OR ');
  const embkPortFilter = portCodes.map((code) => `embkPort.key:"${code}"`).join(' OR ');
  return `(${portOfCallsFilter} OR ${embkPortFilter})`;
}

export function buildFinalResponse(
  excursionResults: SearchResponse,
  itineraryResults: SearchResponse,
  secondItineraryResults?: SearchResponse
) {
  // Merge canvasMap facets (from the first and second itinerary if present)
  const firstCanvasMap = Object.keys(itineraryResults.facets || {}).reduce((acc, key) => {
    if (key.startsWith('canvasMap')) {
      acc[key] = itineraryResults.facets?.[key];
    }
    return acc;
  }, {} as Record<string, any>);

  const secondCanvasMap = secondItineraryResults
    ? Object.keys(secondItineraryResults.facets || {}).reduce((acc, key) => {
        if (key.startsWith('canvasMap')) {
          acc[key] = secondItineraryResults.facets?.[key];
        }
        return acc;
      }, {} as Record<string, any>)
    : {};

  // CanvasMap keys intersection
  const intersectedCanvasMapFacets = Object.keys(firstCanvasMap)
    .filter((key) => key in secondCanvasMap)
    .reduce((acc, key) => {
      acc[key] = firstCanvasMap[key];
      return acc;
    }, {} as Record<string, any>);

  // CanvasMap Combined Facets
  const combinedCanvasMapFacets = secondItineraryResults
    ? intersectedCanvasMapFacets
    : firstCanvasMap;

  // PortCode Intersection
  const excursionsPorts = excursionResults.facets?.['portCode'] || {};
  const portOfCalls = (secondItineraryResults || itineraryResults).facets?.['portOfCalls'] || {};
  const embkPortKeys = (secondItineraryResults || itineraryResults).facets?.['embkPort.key'] || {};
  const allPorts = { ...portOfCalls, ...embkPortKeys };

  const commonKeys = Object.keys(excursionsPorts).filter((key) => key in allPorts);
  const intersectedPorts = commonKeys.reduce((acc, key) => {
    acc[key] = excursionsPorts[key];
    return acc;
  }, {} as Record<string, any>);

  return {
    ...excursionResults,
    facets: {
      ...excursionResults.facets,
      ...combinedCanvasMapFacets,
      ...(Object.keys(intersectedPorts).length > 0 && {
        portCode: intersectedPorts,
      }),
    },
  };
}

// Active UI Destination Filter Helper Function
export function isDestinationFilterActive(): boolean {
  const destinationFilterElement = document.querySelector('[data-filter-type="destination"]');
  const wrapper = destinationFilterElement?.closest('.cmp-search-filter__wrapper');
  return !!destinationFilterElement && !!wrapper && !wrapper.classList.contains('disabled');
}

export function calculateExcDayByCruiseDepartureDate(
  excursionDate: string,
  departureDate: string | undefined
) {
  // return the number of day of the excursions based on the departure date of the cruise booked
  return departureDate ? dayjs(excursionDate).diff(dayjs(departureDate), 'day') + 1 : undefined;
}

export function decodeHtmlEntities(str: string) {
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

// Get the lowest price from a facet object by
// converting the keys to numbers and finding the minimum valid
// price, greater than 0.
function getLowestPriceOfFacetObject(
  facetValue: Record<string, number> | undefined
): number | null {
  if (!facetValue || typeof facetValue !== 'object') return null;
  const allFacetKeys = Object.keys(facetValue)
    .map((facetKey) => Number(facetKey))
    .filter((price) => !Number.isNaN(price) && price > 0);
  return allFacetKeys.length ? Math.min(...allFacetKeys) : null;
}

// Lowest price across all airports according to per cabin or per person filtering
// Used for LINE_MONO to find the overall lowest delta across airports
export function getLowestPriceAcrossAirportsByView(
  facets: Record<string, Record<string, number>> | undefined,
  prefix: string
): number | null {
  if (!facets) return null;
  let overallMin: number | null = null;
  for (const key of Object.keys(facets)) {
    if (!key.startsWith(prefix)) continue;
    const aiportValues = facets[key];
    const minAirportValue = getLowestPriceOfFacetObject(aiportValues);
    if (minAirportValue != null) {
      overallMin = overallMin == null ? minAirportValue : Math.min(overallMin, minAirportValue);
    }
  }
  return overallMin;
}

// Lowest price for a specific airport by price view (per cabin or per person).
// Used for CHIP_MULTI to show delta per airport respecting the selected price view.
export function getLowestPriceForAirportByView(
  facets: Record<string, Record<string, number>> | undefined,
  airportCode: string,
  priceView: string
): number | null {
  const FACET_PREFIX_PER_CABIN = 'flightPricesPerCabin.';
  const FACET_PREFIX_PER_PERSON = 'flightPricesPerPerson.';
  if (!facets || !airportCode || airportCode === 'hasAir') return null;
  const perCabin = getLowestPriceOfFacetObject(facets[`${FACET_PREFIX_PER_CABIN}${airportCode}`]);
  const perPerson = getLowestPriceOfFacetObject(facets[`${FACET_PREFIX_PER_PERSON}${airportCode}`]);
  if (priceView === OrderByTypeEnum.CABIN) return perCabin ?? perPerson;
  if (priceView === OrderByTypeEnum.PERSON) return perPerson ?? perCabin;
  const values = [perCabin, perPerson].filter((n): n is number => n != null);
  return values.length ? Math.min(...values) : null;
}
